package com.milta.feature.imports.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Check
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.CloudUpload
import androidx.compose.material.icons.outlined.HourglassEmpty
import androidx.compose.material.icons.outlined.WifiOff
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.milta.core.localization.ImportsStrings
import com.milta.core.localization.LocalStrings
import com.milta.core.ui.component.MiltaCard
import com.milta.core.ui.theme.ErrorDark
import com.milta.core.ui.theme.SuccessBg
import com.milta.core.ui.theme.SuccessInk
import com.milta.feature.importjobs.presentation.ImportJobFailureKind

/*
 * Result cards of the import analysis screens, moved out of ImportPdfScreen unchanged so the
 * spreadsheet and photo OCR screens show the same cards for the same asynchronous import outcomes.
 */

/** "Service temporairement indisponible" — [banner] says whether the file can be relaunched. */
@Composable
internal fun ImportServiceUnavailableCard(strings: ImportsStrings, banner: String) {
    MiltaCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(20.dp)) {
            ImportResultHeader(
                icon = Icons.Outlined.HourglassEmpty,
                iconBackground = Brush.linearGradient(listOf(WarnGradientStart, WarnGradientEnd)),
                iconTint = WarnIconTint,
                title = strings.serviceUnavailableTitle,
                subtitle = strings.serviceUnavailableSubtitle,
            )
            Spacer(Modifier.height(12.dp))
            ImportResultBanner(text = banner, background = ServiceBg, textColor = ServiceText, glyph = Icons.Outlined.HourglassEmpty)
        }
    }
}

/** "Document illisible" layout — the file itself is the problem, a new file is needed. */
@Composable
internal fun ImportDocumentErrorCard(title: String, subtitle: String, fileName: String, bannerSuffix: String) {
    MiltaCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(20.dp)) {
            ImportResultHeader(
                icon = Icons.Outlined.Close,
                iconBackground = Brush.linearGradient(listOf(MaterialTheme.colorScheme.error, ErrorDark)),
                iconTint = Color.White,
                title = title,
                subtitle = subtitle,
            )
            Spacer(Modifier.height(12.dp))
            ImportResultBanner(
                text = "$fileName — $bannerSuffix",
                background = MaterialTheme.colorScheme.errorContainer,
                textColor = MaterialTheme.colorScheme.onErrorContainer,
                glyph = Icons.Outlined.Close,
            )
        }
    }
}

/** Texts of [ImportDocumentErrorCard] for a failed import's document problem. */
fun ImportsStrings.documentErrorTexts(kind: ImportJobFailureKind): Triple<String, String, String> = when (kind) {
    ImportJobFailureKind.TOO_LONG -> Triple(errorTitleTooLong, errorSubtitleTooLong, errorBannerSuffixTooLong)
    ImportJobFailureKind.TOO_LARGE -> Triple(errorTitleTooLarge, errorSubtitleTooLarge, errorBannerSuffixTooLarge)
    ImportJobFailureKind.NO_DELIVERIES_FOUND -> Triple(errorTitleNoDeliveries, errorSubtitleNoDeliveries, errorBannerSuffixNoDeliveries)
    ImportJobFailureKind.SOURCE_FILE_EXPIRED -> Triple(errorTitleSourceExpired, errorSubtitleSourceExpired, errorBannerSuffixSourceExpired)
    ImportJobFailureKind.ORIGIN_ROUTE_DELETED ->
        Triple(errorTitleOriginRouteDeleted, errorSubtitleOriginRouteDeleted, errorBannerSuffixOriginRouteDeleted)
    // MIL-98 — AI_CREDIT_EXHAUSTED has its own screen (AiCreditExhaustedImportCard); never reached here.
    ImportJobFailureKind.UNREADABLE, ImportJobFailureKind.SERVICE_UNAVAILABLE, ImportJobFailureKind.AI_CREDIT_EXHAUSTED ->
        Triple(errorTitleUnreadable, errorSubtitle, errorBannerSuffixUnreadable)
}

/** Under the steps while the file is being sent (maquette « Envoi photo et annulation », écran 1). */
@Composable
internal fun ImportUploadBanner(text: String) {
    ImportResultBanner(text = text, background = ServiceBg, textColor = ServiceText, glyph = Icons.Outlined.CloudUpload)
}

/** "Import annulé. Aucune livraison n'a été ajoutée." — back on the import menu after any cancel. */
@Composable
internal fun ImportCancelledNotice(text: String) {
    ImportResultBanner(text = text, background = SuccessBg, textColor = SuccessInk, glyph = Icons.Outlined.Check)
}

/**
 * "Annuler cet import ?" — same AlertDialog as the app's other confirmations. [message] says what
 * the cancel really does at this point (analysis stopped, or detected deliveries dropped).
 */
@Composable
internal fun ImportCancelDialog(message: String, dismissLabel: String, onConfirm: () -> Unit, onDismiss: () -> Unit) {
    val imports = LocalStrings.current.imports
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(text = imports.cancelImportDialogTitle, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
        },
        text = {
            Text(text = message, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        },
        confirmButton = {
            TextButton(onClick = onConfirm) {
                Text(text = imports.cancelImportConfirmButton, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.error)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(text = dismissLabel) }
        },
    )
}

/** Review-step cancel message: the detected deliveries are dropped, none is added. */
internal fun ImportsStrings.cancelImportReviewMessage(deliveryCount: Int): String =
    if (deliveryCount == 1) cancelImportReviewMessageSingle else cancelImportReviewMessage.replace("%s", deliveryCount.toString())

/** "Vous êtes hors connexion" — the upload never reached the server. */
@Composable
internal fun ImportOfflineCard(strings: ImportsStrings, fileName: String, fileIcon: ImageVector) {
    MiltaCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(20.dp)) {
            ImportResultHeader(
                icon = Icons.Outlined.WifiOff,
                iconBackground = Brush.linearGradient(listOf(OfflineBorder, OfflineBorder)),
                iconTint = OfflineText,
                title = strings.offlineTitle,
                subtitle = strings.offlineSubtitle,
            )
            Spacer(Modifier.height(14.dp))
            ImportFileChip(fileName = fileName, subtitle = strings.fileWaitingConnectionLabel, icon = fileIcon)
            Spacer(Modifier.height(12.dp))
            ImportResultBanner(text = strings.offlineBanner, background = OfflineBg, textColor = OfflineText, glyph = Icons.Outlined.WifiOff)
        }
    }
}

@Composable
internal fun ImportResultHeader(icon: ImageVector, iconBackground: Brush, iconTint: Color, title: String, subtitle: String) {
    Row(verticalAlignment = Alignment.Top, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
        Box(
            modifier = Modifier.size(44.dp).background(iconBackground, RoundedCornerShape(12.dp)),
            contentAlignment = Alignment.Center,
        ) {
            Icon(icon, contentDescription = null, tint = iconTint, modifier = Modifier.size(20.dp))
        }
        Column {
            Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.ExtraBold, color = MaterialTheme.colorScheme.onSurface)
            Spacer(Modifier.height(2.dp))
            Text(subtitle, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
    }
}

@Composable
internal fun ImportResultBanner(text: String, background: Color, textColor: Color, glyph: ImageVector) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(background, RoundedCornerShape(12.dp))
            .padding(horizontal = 14.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Icon(glyph, contentDescription = null, tint = textColor, modifier = Modifier.size(16.dp))
        Text(text, style = MaterialTheme.typography.bodySmall, color = textColor)
    }
}

@Composable
private fun ImportFileChip(fileName: String, subtitle: String, icon: ImageVector) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.background, RoundedCornerShape(12.dp))
            .padding(horizontal = 14.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Box(
            modifier = Modifier.size(40.dp).background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(10.dp)),
            contentAlignment = Alignment.Center,
        ) {
            Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = fileName,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = subtitle,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

// ── Palettes locales (mêmes tons que ImportScreen — hors Color.kt) ───────

private val WarnGradientStart = Color(0xFFE9C387)
private val WarnGradientEnd = Color(0xFFE0BF98)
private val WarnIconTint = Color(0xFF6B4A12)

private val ServiceBg = Color(0xFFF2E9DE)
private val ServiceText = Color(0xFF8A5A2C)

private val OfflineBg = Color(0xFFEEF0F2)
private val OfflineText = Color(0xFF57646C)
private val OfflineBorder = Color(0xFFC9D0D5)
